import Excel from "exceljs";
import saveAs from "file-saver";

const DEFAULT_HEADER_HEIGHT = 30;
const HR_BLACK_HEADERS_STYLES = {
    style: {
        alignment: { vertical: 'middle', horizontal: 'left', wrapText: true },
    },
    fill: {
        type: 'pattern',
        pattern:'solid',
        fgColor: {argb: '00000000'},
        bgColor: {argb: '00000000'}
    },
    font: {
        color: {argb: '00ffffff'},
        family: 2,
        size: 10,
        name: 'Arial',
        bold: true
    },
};

const HR_BLACK_HEADERS_DYNAMIC_STYLE = { alignment: { vertical: 'middle', horizontal: 'center', wrapText: true } };

const TOTAL_BORDERS = {
    style: 'thick', color: {argb: '00000000'}
};

const ARIAL_10 = {
    color: {argb: '00000000'},
    family: 2,
    size: 10,
    name: 'Arial'
}

const COLUMN_TYPES = ['utilization_rate', 'parameter1', 'parameter2', 'open_utilization'];

class ExportRoasServiceClass {
    constructor() {
        this.token = '';
    }

    createBook(username) {
        const wb = new Excel.Workbook();
        wb.creator = 'Riverside KPI App';
        wb.lastModifiedBy = username;
        wb.created = new Date();

        return wb;
    }

    async downloadBook(workbook, filename) {
        await workbook.xlsx.writeBuffer().then(function(buffer) {
            saveAs(
                new Blob([buffer], { type: "application/octet-stream" }),
                filename + '.xlsx'
            );
        });
    }

    async downloadIndividual(data, username, filename) {
        const workbook = this.createBook(username);
        const pages = ['Summary', 'Quarterly Actuals','Actuals vs Budgets', 'Skills'];
        const DEFAULT_COLUMNS = [
            { header: 'REGION #', key: 'region', width: 12 },
            { header: 'Employee #', key: 'employee_number', width: 14 },
            { header: 'NAME', key: 'name', width: 35 },
            { header: 'OFFICE', key: 'office', width: 20 },
            { header: 'JOB TITLE', key: 'position', width: 40 },
            { header: 'FULL TIME /\nPART TIME', key: 'time', width: 17 },
            { header: 'START\nDATE', key: 'start_date_formatted', width: 12 },
            { header: 'END\nDATE', key: 'end_date_formatted', width: 12 },
        ];
        const END_DATE_WIDTH = 20; // Used to show total titles in actual vs budgets report

        pages.map((pageTitle, pageNumber) => {
            const isActualVsBudgets = pageTitle === 'Actuals vs Budgets';
            const sheet = workbook.addWorksheet(pageTitle);

            let employeeCols = [];
            let customHeaders = [];

            if (!isActualVsBudgets) employeeCols.push({ header: 'QUARTILE', key: 'quartile', width: 17 });
            // Add custom columns depending on page / sub report / sheet
            switch (pageTitle) {
                case 'Summary':
                case 'Quarterly Actuals':
                    const dynamicHeaders = pageTitle === 'Summary' ? data.headers : data.quarterly_headers;
                    dynamicHeaders.map(header => {
                        let width = 13;
                        if (header === 'last_12_mo') {
                            width = 15;
                            header = header.replaceAll('_',' ');
                        }
                        employeeCols.push({ header: 'UTILIZATION RATE', key: header, width })
                        customHeaders.push(header.toUpperCase());
                    });
                    break;
                case 'Actuals vs Budgets':
                    data.headers.map(header => {
                        COLUMN_TYPES.map(field =>{
                            // header value is set later per role
                            employeeCols.push({ header: '', key: `${header}_${field}`, width: 10 });
                        })
                        // 3 empty columns to merge it into 4 for: utilization rate, actual, budget and open utilization
                        customHeaders.push(header.replaceAll('_', ' ').toUpperCase(), '','','');
                    });
                    break;
                case 'Skills':
                    employeeCols.push(
                        { header: 'LAST 12 MO', key: 'last_12_mo', width: 15 },
                        { header: 'SKILLS', key: 'skills', width: 100 }
                    );
                    customHeaders.push('','');
            }

            let headerColumns = [...DEFAULT_COLUMNS, ...employeeCols];

            // columns default size
            var columnsKey = [];
            for (let columnIndex = 0; columnIndex < headerColumns.length; columnIndex++) {
                const column = headerColumns[columnIndex];
                columnsKey.push({key: column.key, width: (isActualVsBudgets) ? END_DATE_WIDTH : column.width});
            }
            sheet.columns = columnsKey;
            sheet.font = {
                family: 2,
                name: 'Arial',
                size: 10
            };

            data.roles.map((role, roleIndex) => {
                // role global variables
                const roleTarget = data.budgets[roleIndex];
                const roleStyle = role.style;

                let emptyColumns = ['','','','','','',''];
                if (!isActualVsBudgets) emptyColumns.push(''); 
                let roleTitleRow = sheet.addRow([`${role.title} - HEADCOUNT: ${role.employees.length}`, ...emptyColumns, ...customHeaders]);
                // merge first row with role / kpi title - first 5 columns merged in second row
                const lastColumnIndex = isActualVsBudgets ? DEFAULT_COLUMNS.length : DEFAULT_COLUMNS.length + 1;
                sheet.mergeCells(`${roleTitleRow.getCell(1).address}: ${roleTitleRow.getCell(lastColumnIndex).address}`);
                sheet.views = [{ state: 'frozen', xSplit: lastColumnIndex -3, ySplit: 1}];

                let headersRow = sheet.addRow();

                for (let columnIndex = 0; columnIndex < headerColumns.length; columnIndex++) {
                    const column = headerColumns[columnIndex];
                    [roleTitleRow, headersRow].map((row, index) => {
                        const cell = row.getCell(columnIndex+1);
                        if (index === 1) {
                            cell.value = column.header;
                            cell.width = column.width;
                        }
                        cell.style = (columnIndex >= lastColumnIndex) ? HR_BLACK_HEADERS_DYNAMIC_STYLE : HR_BLACK_HEADERS_STYLES.style;
                        cell.fill = HR_BLACK_HEADERS_STYLES.fill;
                        cell.font = HR_BLACK_HEADERS_STYLES.font;
                    });
                }

                if (isActualVsBudgets) {
                    // merge actual vs budgets months headers
                    let mergeIndex = lastColumnIndex + 1;
                    data.headers.map( (header, index) => {
                        sheet.mergeCells(`${roleTitleRow.getCell(mergeIndex).address}:${roleTitleRow.getCell(mergeIndex + 3).address}`);
                        // set custom role titles
                        ['utilization rate', role.actual_title, role.budget_title, role.delta_title].map(title =>{
                            headersRow.getCell(mergeIndex).value = title.toUpperCase();
                            mergeIndex++;
                        })
                    });
                }

                headersRow.height = DEFAULT_HEADER_HEIGHT;
                roleTitleRow.border = {
                    bottom: {style: 'thick', color: {argb: '00FFFFFF'}}
                };

                headersRow.border = null;

                if (role.employees.length > 0) {
                    // Employees data
                    role.employees.map((employee, employeeIndex) => {
                        let columnIndex = lastColumnIndex + 1;
                        let employeeRow = sheet.addRow(employee);
                        // set dynamic columns
                        switch(pageTitle) {
                            case 'Summary':
                            case 'Quarterly Actuals':
                                const dynamicHeaders = pageTitle === 'Summary' ? data.headers : data.quarterly_headers;
                                dynamicHeaders.map(header => {
                                    const value = employee.employeeData[header].utilization_rate;
                                    employeeRow.getCell(columnIndex).value = value;
                                    if (header === 'last_12_mo') 
                                        employeeRow.getCell(columnIndex).border = {left: TOTAL_BORDERS, right: TOTAL_BORDERS};
                                    const cellStyle = getUtilizationColor(value, roleTarget[header], roleStyle);
                                    setCellColors(employeeRow.getCell(columnIndex), cellStyle);
                                    columnIndex++;
                                });
                                break;
                            case 'Actuals vs Budgets':
                                data.headers.map(header => {
                                    COLUMN_TYPES.map(field =>{
                                        const value = employee.employeeData[header][field];
                                        employeeRow.getCell(columnIndex).value = value;
                                        if (field === 'utilization_rate') {
                                            const cellStyle = getUtilizationColor(value, roleTarget[header], roleStyle);
                                            setCellColors(employeeRow.getCell(columnIndex), cellStyle);
                                        }
                                        columnIndex++;
                                    });
                                    if (header === 'last_12_mo') 
                                        employeeRow.getCell(columnIndex).border = {left: TOTAL_BORDERS, right: TOTAL_BORDERS};
                                })
                                break;
                            case 'Skills':
                                const value = employee.employeeData.last_12_mo.utilization_rate;
                                employeeRow.getCell(columnIndex).value = value;
                                employeeRow.getCell(columnIndex).border = {left: TOTAL_BORDERS, right: TOTAL_BORDERS};
                                const cellStyle = getUtilizationColor(value, roleTarget.last_12_mo, roleStyle);
                                setCellColors(employeeRow.getCell(columnIndex), cellStyle);
                        }
                        // set static cells style
                        for (let cellIndex = 1; cellIndex <= lastColumnIndex; cellIndex++) {
                            const cell = employeeRow.getCell(cellIndex);
                            setCellColors(cell, {background: '00F2F2F2', text: '00000000'});
                        }
                    });

                    // add totals rows
                    const totals = data.totals[roleIndex];
                    const py = data.py[roleIndex];
                    const target = data.budgets[roleIndex];

                    if (pageTitle !== 'Skills') {
                        let indexStart = lastColumnIndex + 1;
                        let totalsRow = null;

                        if (isActualVsBudgets) {
                            totalsRow = sheet.addRow();
                            totalsRow.getCell(lastColumnIndex).value = 'TOTALS';
                            totalsRow.border = { top: TOTAL_BORDERS };
                        }

                        let avgTotalRow = sheet.addRow();
                        avgTotalRow.getCell(lastColumnIndex).value = 'AVERAGE';

                        if (!isActualVsBudgets) avgTotalRow.border = { top: TOTAL_BORDERS };

                        let yearOverYearRow = sheet.addRow();
                        yearOverYearRow.getCell(lastColumnIndex).value = 'YEAR OVER YEAR';

                        let targetRow = sheet.addRow();
                        targetRow.getCell(lastColumnIndex).value = 'TARGET';

                        // set totals data
                        const headers = (pageTitle === 'Quarterly Actuals') ? data.quarterly_headers : data.headers;
                        const subHeaders = isActualVsBudgets ? COLUMN_TYPES : ['utilization_rate'];
                        headers.map(header => {
                            subHeaders.map(subHeader => {
                                const isUtilizationRate = subHeader === 'utilization_rate';
                                if (isActualVsBudgets)
                                    totalsRow.getCell(indexStart).value  = getTotalFormatted(
                                        totals[header][subHeader],
                                        'total',
                                        header,
                                        isUtilizationRate
                                    );
                                
                                // Average
                                const avgValue = getTotalFormatted(
                                    totals[header][subHeader],
                                    'avg',
                                    header,
                                    isUtilizationRate,
                                    role.employees.length
                                );

                                avgTotalRow.getCell(indexStart).value = avgValue;
                                const avgCellStyle = getUtilizationColor(avgValue, roleTarget[header], roleStyle);
                                setCellColors(avgTotalRow.getCell(indexStart), avgCellStyle);

                                // Year Over Year
                                const yearOverYearValue = getTotalFormatted(
                                    py[header][subHeader],
                                    'year_over_year',
                                    header,
                                    isUtilizationRate,
                                    role.employees.length
                                );

                                yearOverYearRow.getCell(indexStart).value = yearOverYearValue;
                                const yearOverYearCellStyle = getUtilizationColor(yearOverYearValue, roleTarget[header], roleStyle);
                                setCellColors(yearOverYearRow.getCell(indexStart), yearOverYearCellStyle);

                                // Target
                                targetRow.getCell(indexStart).value = getTotalFormatted(
                                    target[header],
                                    'target',
                                    header,
                                    isUtilizationRate,
                                    role.employees.length
                                );
                                indexStart++;
                            })
                        })
                    }
                }

                let emptyRow = sheet.addRow([]);
                emptyRow.fill = {
                    type: 'pattern',
                    pattern:'solid',
                    fgColor: {argb: '00FFFFFF'},
                    bgColor: {argb: '00FFFFFF'}
                };

                emptyRow.commit();
            })
        });
        await this.downloadBook(workbook, filename);
    }
}

const getTotalFormatted = (total, totalType, column, isUtilizationRate, employees = null) => {
    if (totalType === 'total' && isUtilizationRate) return '--';
    let totalClean = parseFloat(total);
    if (totalType === 'avg' && employees > 0) totalClean /= employees;
    if (isUtilizationRate) totalClean = totalClean.toFixed(2) + '%';
    if ((totalType === 'target' && !isUtilizationRate )
        || (['target', 'year_over_year'].includes(totalType) && column === 'last_12_mo'))
        totalClean = '--';
    return totalClean;
}

const getUtilizationColor = (value, budget, style) => {
    if (!style || !JSON.parse(style) || value === '--') return getClassColors('white');
    budget = parseFloat(budget);
    style = JSON.parse(style);
    value = parseFloat(value);

    switch (true) {
        case Math.abs(Math.abs(value) - Math.abs(budget)) <= style.range1:
            return getClassColors(style.range1_color);
        
        case Math.abs(Math.abs(value) - Math.abs(budget)) <= style.range2:
            return getClassColors(style.range2_color);

        default:
            return getClassColors(style.else_color);
    }
}

const getClassColors = (customClass) => {
    let colors = {
        text: '00000000',
        background: '00FFFFFF'
    };

    switch(customClass) {
        case 'red': 
            colors.background = '00FFC7Ce';
            break;
        case 'green': 
            colors.background = 'C6EFCE';
            break;
        case 'blue': 
            colors.background = 'D2E6FF';
            break;
        case 'yellow': 
            colors.background = 'FFFFE0';
            break;
        case 'grey': 
        colors = {background: '00F2F2F2', text: '00000000'};
            break;
        case 'white': 
            colors = {background: '00FFFFFF', text: '00000000'};
            break;
    }

	return colors;
}

const setCellColors = (cell, style) => {
    cell.fill = {
        type: 'pattern',
        pattern:'solid',
        fgColor: {argb: style.background},
        bgColor: {argb: style.background}
    }

    cell.font = {
        family: 2,
        name: 'Calibri',
        size: 11,
        color: {argb: style.text}
    };
}

let ExportRoasService = new ExportRoasServiceClass(ENVIRONMENT.API_HOST);
export default ExportRoasService;